import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, CardContent, Dialog, DialogContent, Grid, Icon, Link, Tooltip, Typography } from '@mui/material'
import { compact, isNil, orderBy, sortBy } from 'lodash'
import { toast } from 'react-toastify'
import useTitles from 'common/hooks/useTitles'
import useFetch from 'common/hooks/useFetch'
import useActions from 'common/hooks/useActions'
import { NEW, REFRESH } from 'common/components/action/ActionConstant'
import { STATUS } from 'common/constants/storeConstants'
import { aquaFetch } from 'common/utils/ActionUtils'
import ApplicationConf from 'conf/ApplicationConf'
import { ALIGN, CardTable } from 'common/components/datatable/Table'
import TabsSideBar from 'common/components/tab/TabsSideBar'
import Input from 'common/components/form/Input'
import Select from 'common/components/form/Select'
import Checkbox from 'common/components/form/Checkbox'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import { Circle } from '@mui/icons-material'
import { truncateString } from 'common/utils/StringUtil'

// États d'un sujet (valeur en base) + couleur (reprend le code couleur de l'ancien Excel).
const ETATS = [
    { value: 0, label: 'En attente', color: '#f5cd56' },
    { value: 1, label: 'A faire', color: '#ed7d31' },
    { value: 2, label: 'En cours', color: '#4472c4' },
    { value: 3, label: 'A vérifier', color: '#70ad47' },
    { value: 4, label: 'Terminé', color: '#7f7f7f' },
]
const etatDef = (etat) => ETATS.find(e => e.value === etat)
// Item de Select : pastille ronde (style clients) + libellé, alignés verticalement.
const etatItemLabel = (e) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Circle sx={{ color: e.color, fontSize: '1rem', marginRight: '6px' }} />
        {e.label}
    </Box>
)
const ETAT_ITEMS = ETATS.map(e => ({ value: e.value, label: etatItemLabel(e) }))

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
const moisLabel = (m) => (m >= 1 && m <= 12 ? MOIS[m - 1] : '')
const MOIS_ITEMS = MOIS.map((label, i) => ({ value: i + 1, label }))
const moisRange = (p) => compact([moisLabel(p.moisDebut), p.moisFin && p.moisFin !== p.moisDebut ? moisLabel(p.moisFin) : undefined]).join(' → ')

// Rend les n° de ticket cliquables (ouverture Mantis dans un nouvel onglet) ; le reste (texte) reste inchangé.
const MANTIS_TICKET_URL = 'https://support.aquasys.fr/view.php?id='
const renderTicketRefs = (tickets) =>
    compact((tickets ?? '').split(/(\d+)/)).map((part, i) => (/^\d+$/.test(part)
        ? <Link key={i} href={`${MANTIS_TICKET_URL}${part}`} target='_blank' rel='noopener' onClick={e => e.stopPropagation()}>{part}</Link>
        : part))

// Lot cliquable -> Suivi des lots, nom du lot pré-rempli dans le champ de recherche.
// On ouvre via window.open (comme l'écran des stats) : l'onglet garde son opener et hérite donc du
// sessionStorage (token d'auth) de l'onglet courant. Un <a target=_blank> demandait une reconnexion.
const openLotFollowUp = (e, lot) => {
    e.stopPropagation()
    window.open(`/devs?search=${encodeURIComponent(lot)}`, '_blank')
}
const renderLot = (lot) => isNil(lot)
    ? ''
    : <Link component='button' type='button' sx={{ verticalAlign: 'baseline' }} onClick={e => openLotFollowUp(e, lot)}>{lot}</Link>

// Fiche client dans un nouvel onglet (même mécanique que les lots : window.open garde l'opener → auth conservée).
const openCustomer = (e, id) => {
    e.stopPropagation()
    window.open(`/customer/${id}`, '_blank')
}

// Pastille d'état colorée.
const EtatChip = ({ etat }) => {
    const def = etatDef(etat)
    if (!def) return null
    return (
        <Box sx={{ display: 'inline-flex', px: 1, py: '3px', borderRadius: '2px', backgroundColor: def.color, color: '#fff', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {def.label}
        </Box>
    )
}
EtatChip.propTypes = { etat: PropTypes.number }

// -------------------------------------------------------------------------------------------------
// Dialog : fichier de priorités (nom + plage de mois)
// -------------------------------------------------------------------------------------------------
const PrioriteFileDialog = ({ priorite, onClose, onSaved }) => {
    const [form, setForm] = useState(priorite ?? { moisDebut: new Date().getMonth() + 1, moisFin: new Date().getMonth() + 1 })
    const change = (c) => setForm(f => ({ ...f, ...c }))
    const isEdit = !isNil(form.id)

    const save = () => {
        aquaFetch(ApplicationConf.gdl.priorite(), { method: isEdit ? 'PUT' : 'POST', body: { ...form, sujets: form.sujets ?? [] } })
            .then(() => {
                onSaved(); onClose()
            })
            .catch(() => toast.error('Erreur lors de l\'enregistrement des priorités'))
    }

    return (
        <Dialog open maxWidth='sm' fullWidth>
            <BasicDialogTitle onClose={onClose}>{isEdit ? 'Modifier le fichier de priorités' : 'Nouveau fichier de priorités'}</BasicDialogTitle>
            <DialogContent>
                <Grid container columnSpacing={2} rowSpacing={1} sx={{ paddingTop: 1 }}>
                    <Grid size={12}><Input label='Nom' value={form.nom} onChange={v => change({ nom: v })} required /></Grid>
                    <Grid size={6}><Select label='Mois de début' items={MOIS_ITEMS} value={form.moisDebut} onChange={v => change({ moisDebut: v })} sortItems={false} /></Grid>
                    <Grid size={6}><Select label='Mois de fin' items={MOIS_ITEMS} value={form.moisFin} onChange={v => change({ moisFin: v })} sortItems={false} /></Grid>
                </Grid>
            </DialogContent>
            <BasicDialogAction buttonLabel={isEdit ? 'Modifier' : 'Créer'} onValidate={save} isDisabled={!form.nom} />
        </Dialog>
    )
}
PrioriteFileDialog.propTypes = { priorite: PropTypes.shape({}), onClose: PropTypes.func, onSaved: PropTypes.func }

// -------------------------------------------------------------------------------------------------
// Dialog : sujet
// -------------------------------------------------------------------------------------------------
const SujetDialog = ({ sujet, idPriorite, customers = [], onClose, onSaved }) => {
    const [form, setForm] = useState(sujet ?? { idPriorite, etat: 1, specs: false, clients: [] })
    const change = (c) => setForm(f => ({ ...f, ...c }))
    const isEdit = !isNil(form.id)

    const clientItems = useMemo(() => sortBy(customers.map(c => ({ value: c.id, label: c.mnemonic || (c.name?.value ?? c.name) })), 'label'), [customers])

    const save = () => {
        aquaFetch(ApplicationConf.gdl.prioriteSujet(), { method: isEdit ? 'PUT' : 'POST', body: { ...form, idPriorite } })
            .then(() => {
                onSaved(); onClose()
            })
            .catch(() => toast.error('Erreur lors de l\'enregistrement du sujet'))
    }

    return (
        <Dialog open maxWidth='md' fullWidth>
            <BasicDialogTitle onClose={onClose}>{isEdit ? 'Modifier le sujet' : 'Nouveau sujet'}</BasicDialogTitle>
            <DialogContent>
                <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' sx={{ paddingTop: 1 }}>
                    <Grid size={6}><Input label='Fonctionnalités' value={form.fonctionnalites} onChange={v => change({ fonctionnalites: v })} /></Grid>
                    <Grid size={6}><Select multiple label='Clients' value={form.clients ?? []} items={clientItems} onChange={v => change({ clients: v })} /></Grid>
                    <Grid size={12}><Input label='Description' value={form.description} onChange={v => change({ description: v })} multiline /></Grid>
                    <Grid size={3}><Select label='État' items={ETAT_ITEMS} value={form.etat} onChange={v => change({ etat: v })} sortItems={false} /></Grid>
                    <Grid size={3}><Input label='Échéance' value={form.echeance} onChange={v => change({ echeance: v })} /></Grid>
                    <Grid size={3}><Input label='Priorité' type='number' value={form.priorite} onChange={v => change({ priorite: isNil(v) || v === '' ? undefined : Number(v) })} /></Grid>
                    <Grid size={3}><Checkbox label='Spec' checked={!!form.specs} onToggle={() => change({ specs: !form.specs })} /></Grid>
                    <Grid size={4}><Input label='Dev' value={form.dev} onChange={v => change({ dev: v })} /></Grid>
                    <Grid size={4}><Input label='CDP' value={form.cdp} onChange={v => change({ cdp: v })} /></Grid>
                    <Grid size={4}><Input label='Lot' value={form.lot} onChange={v => change({ lot: v })} /></Grid>
                    <Grid size={12}><Input label='Tickets' value={form.tickets} onChange={v => change({ tickets: v })} /></Grid>
                </Grid>
            </DialogContent>
            <BasicDialogAction buttonLabel={isEdit ? 'Modifier' : 'Créer'} onValidate={save} />
        </Dialog>
    )
}
SujetDialog.propTypes = {
    sujet: PropTypes.shape({}), idPriorite: PropTypes.number, customers: PropTypes.arrayOf(PropTypes.shape({})),
    onClose: PropTypes.func, onSaved: PropTypes.func,
}

// -------------------------------------------------------------------------------------------------
// Tableau des sujets d'un fichier de priorités (composant table maison)
// -------------------------------------------------------------------------------------------------
const SUJETS_HEADERS = [
    { key: 'clients', value: 'Client' },
    { key: 'fonctionnalites', value: 'Fonctionnalités' },
    { key: 'description', value: 'Description' },
    { key: 'etat', value: 'État' },
    { key: 'echeance', value: 'Échéance' },
    { key: 'priorite', value: 'Priorité' },
    { key: 'dev', value: 'Dev' },
    { key: 'specs', value: 'Spec' },
    { key: 'cdp', value: 'CDP' },
    { key: 'ticketsAndLots', value: 'Ticket / Lots' },
]

const SujetsTable = ({ priorite, clientById, onAddSujet, onEditSujet, onDeleteSujet, onEditFile, onDeleteFile, onChangeEtat }) => {
    // Mnémonique s'il existe, sinon nom.
    const clientDisplay = (id) => {
        const c = clientById[id]
        return isNil(c) ? undefined : (c.mnemonic || c.name)
    }
    // Nom du client cliquable -> fiche client dans un nouvel onglet (comme les lots).
    const renderClient = (id) => {
        const label = clientDisplay(id)
        return isNil(label) ? null : (
            <Link key={id} component='button' type='button' sx={{ display: 'block', textAlign: 'left', whiteSpace: 'nowrap' }} onClick={e => openCustomer(e, id)}>{label}</Link>
        )
    }
    const rows = (priorite.sujets ?? []).map(s => ({
        sujetRef: s,
        clients: {
            value: <Box>{(s.clients ?? []).map(renderClient)}</Box>,
            sortValue: compact((s.clients ?? []).map(clientDisplay)).join(', '),
        },
        fonctionnalites: s.fonctionnalites ?? '',
        description: {
            value: <Tooltip title={s.description ?? ''}><span>{truncateString(s.description ?? '', 60)}</span></Tooltip>,
            sortValue: s.description ?? '',
        },
        etat: { value: <EtatChip etat={s.etat} />, sortValue: s.etat },
        echeance: s.echeance ?? '',
        priorite: { value: isNil(s.priorite) ? '' : `${s.priorite}`, sortValue: s.priorite ?? 9999, align: ALIGN.CENTER },
        dev: s.dev ?? '',
        specs: { value: s.specs ? <Icon sx={{ color: '#70ad47' }}>check</Icon> : '', sortValue: s.specs ? 1 : 0, align: ALIGN.CENTER },
        cdp: s.cdp ?? '',
        ticketsAndLots: {
            value: <span>{renderTicketRefs(s.tickets)}{!isNil(s.tickets) && !isNil(s.lot) ? ' / ' : ''}{renderLot(s.lot)}</span>,
            sortValue: compact([s.tickets, s.lot]).join(' / '),
        },
    }))

    const contextActions = (row) => [
        { label: 'Modifier le sujet', icon: 'edit', onClick: () => onEditSujet(row.sujetRef) },
        ...ETATS.map(e => ({
            label: `Passer « ${e.label} »`,
            icon: <Circle sx={{ color: e.color, fontSize: '1rem' }} />,
            displayed: row.sujetRef.etat !== e.value,
            onClick: () => onChangeEtat({ ...row.sujetRef, idPriorite: priorite.id }, e.value),
        })),
        { label: 'Supprimer le sujet', icon: 'delete', color: 'red', onClick: () => onDeleteSujet(row.sujetRef) },
    ]

    return (
        <CardTable
            title={moisRange(priorite)}
            color='#161832'
            rows={rows}
            headers={SUJETS_HEADERS}
            contextActions={contextActions}
            filterable={false}
            rowsPerPageDefault={-1}
            actions={[
                { icon: 'add', tooltip: 'Ajouter un sujet', onClick: onAddSujet },
                { icon: 'edit', tooltip: 'Modifier le fichier', onClick: onEditFile },
                { icon: 'delete', tooltip: 'Supprimer le fichier', onClick: onDeleteFile },
            ]}
        />
    )
}
SujetsTable.propTypes = {
    priorite: PropTypes.object,
    clientById: PropTypes.object,
    onAddSujet: PropTypes.func, onEditSujet: PropTypes.func, onDeleteSujet: PropTypes.func,
    onEditFile: PropTypes.func, onDeleteFile: PropTypes.func, onChangeEtat: PropTypes.func,
}

// -------------------------------------------------------------------------------------------------
// Écran Priorités : cartes des fichiers à gauche (TabsSideBar), tableau des sujets à droite.
// -------------------------------------------------------------------------------------------------
const PrioritesApp = () => {
    const { data: priorites = [], reload, status } = useFetch(ApplicationConf.gdl.priorites())
    const { data: customers = [] } = useFetch(ApplicationConf.gdl.customers())

    const clientById = useMemo(() => {
        const map = {}
        customers.forEach(c => {
            map[c.id] = { name: c.name?.value ?? c.name, mnemonic: c.mnemonic }
        })
        return map
    }, [customers])

    // Tri des fichiers de priorités (menu de gauche) par mois de début puis mois de fin, du plus récent au plus ancien.
    const sortedPriorites = useMemo(() => orderBy(priorites, ['moisDebut', 'moisFin'], ['desc', 'desc']), [priorites])

    const [selectedId, setSelectedId] = useState(undefined)
    const selected = useMemo(() => sortedPriorites.find(p => p.id === selectedId) ?? sortedPriorites[0], [sortedPriorites, selectedId])

    useTitles(() => [{
        label: 'Roadmap',
        href: '/roadmap',
    }, {
        label: 'Priorités',
        href: '/roadmap/priorites',
    }, {
        label: selected?.nom ?? 'Aucun fichier',
        href: '/roadmap/priorites',
    }], [selected?.nom])

    const [fileDialog, setFileDialog] = useState(undefined)
    const [sujetDialog, setSujetDialog] = useState(undefined)
    const [sujetToDelete, setSujetToDelete] = useState(undefined)
    const [fileToDelete, setFileToDelete] = useState(undefined)

    useActions(() => [
        { type: NEW, tooltip: 'Nouveau fichier de priorités', onClick: () => setFileDialog({}) },
        { type: REFRESH, onClick: reload, loading: status === STATUS.LOADING },
    ], [status])

    const changeEtat = (sujet, etat) => {
        aquaFetch(ApplicationConf.gdl.prioriteSujet(), { method: 'PUT', body: { ...sujet, etat } })
            .then(reload)
            .catch(() => toast.error('Erreur lors de la modification de l\'état'))
    }

    const deleteSujet = () => {
        aquaFetch(ApplicationConf.gdl.prioriteSujetById(sujetToDelete.id), { method: 'DELETE' })
            .then(() => {
                setSujetToDelete(undefined); reload()
            })
            .catch(() => toast.error('Erreur lors de la suppression du sujet'))
    }

    const deleteFile = () => {
        aquaFetch(ApplicationConf.gdl.prioriteById(fileToDelete.id), { method: 'DELETE' })
            .then(() => {
                setFileToDelete(undefined); reload()
            })
            .catch(() => toast.error('Erreur lors de la suppression du fichier de priorités'))
    }

    return (
        <Grid container spacing={2}>
            {priorites.length === 0 ? (
                <Grid size={12}>
                    <Card>
                        <CardContent>
                            <Typography color='text.secondary'>Aucun fichier de priorités. Créez-en un pour commencer.</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            ) : (
                <Grid size={12}>
                    <TabsSideBar
                        fitContent
                        defaultTab={selected?.id}
                        onChangeTab={setSelectedId}
                        tabs={sortedPriorites.map(p => ({
                            id: p.id,
                            title: <>{p.nom}<br />({moisRange(p)})</>,
                            icon: 'flag',
                            count: (p.sujets ?? []).length,
                            countLabel: 'sujet',
                            content: (
                                <SujetsTable
                                    priorite={p}
                                    clientById={clientById}
                                    onAddSujet={() => setSujetDialog({ idPriorite: p.id })}
                                    onEditSujet={(s) => setSujetDialog(s)}
                                    onDeleteSujet={setSujetToDelete}
                                    onEditFile={() => setFileDialog(p)}
                                    onDeleteFile={() => setFileToDelete(p)}
                                    onChangeEtat={changeEtat}
                                />
                            ),
                        }))}
                    />
                </Grid>
            )}
            {!isNil(fileDialog) && (
                <PrioriteFileDialog
                    priorite={fileDialog.id ? fileDialog : undefined}
                    onClose={() => setFileDialog(undefined)}
                    onSaved={reload}
                />
            )}
            {!isNil(sujetDialog) && (
                <SujetDialog
                    sujet={sujetDialog.id ? sujetDialog : undefined}
                    idPriorite={sujetDialog.idPriorite}
                    customers={customers}
                    onClose={() => setSujetDialog(undefined)}
                    onSaved={reload}
                />
            )}
            <ConfirmModal
                isOpen={!isNil(sujetToDelete)}
                title='Supprimer ce sujet ?'
                content={!isNil(sujetToDelete) && (
                    <>
                        <b>{sujetToDelete.fonctionnalites}</b>
                        {!isNil(sujetToDelete.description) && <><br />{sujetToDelete.description}</>}
                    </>
                )}
                onValidate={deleteSujet}
                onClose={() => setSujetToDelete(undefined)}
            />
            <ConfirmModal
                isOpen={!isNil(fileToDelete)}
                title={`Supprimer le fichier « ${fileToDelete?.nom ?? ''} » et tous ses sujets ?`}
                onValidate={deleteFile}
                onClose={() => setFileToDelete(undefined)}
            />
        </Grid>
    )
}

export default PrioritesApp
